// Stream + channel badges for the Merlin Visibility Pane.
//
// One colour per stream so a row reads at a glance ("that's a widget chat",
// "that's the order extractor"); the same tones the Agent Topology page uses
// for its node kinds, so the two admin views agree.

import type { ChannelBindingSummary, ChannelKind, StreamKind } from '../../services/merlinMonitor';
import type { ConversationState } from '../../services/agentConsole';
import { channelTitle, streamTone } from './monitorDisplay';
import styles from './StreamBadge.module.css';

export function StreamBadge({ stream, label }: { stream: StreamKind; label: string }) {
  const tone = streamTone(stream);
  return (
    <span
      className={styles.badge}
      style={{ background: tone.bg, color: tone.ink }}
      data-stream={stream}
      title={`Stream: ${label}`}
    >
      {label}
    </span>
  );
}

const CHANNEL_GLYPH: Record<ChannelKind, string> = {
  email: '✉',
  sms: '💬',
  slack: '#',
  widget: '◫',
};

/** The external media a conversation is held over, as compact chips. */
export function ChannelChips({ channels }: { channels: ChannelBindingSummary[] }) {
  if (channels.length === 0) return null;
  return (
    <span className={styles.chips}>
      {channels.map((b) => (
        <span key={b.id} className={styles.chip} data-channel={b.channel} title={channelTitle(b)}>
          <span aria-hidden="true">{CHANNEL_GLYPH[b.channel] ?? '•'}</span> {b.channel}
        </span>
      ))}
    </span>
  );
}

const STATE_TONE: Record<ConversationState, string> = {
  created: styles.stateNeutral,
  running: styles.stateLive,
  awaiting_input: styles.stateNeutral,
  failed: styles.stateBad,
  cancelled: styles.stateMuted,
  archived: styles.stateMuted,
};

export function StateBadge({ state }: { state: ConversationState }) {
  return (
    <span className={`${styles.state} ${STATE_TONE[state] ?? styles.stateNeutral}`} data-state={state}>
      {state === 'running' && <span className={styles.pulse} aria-hidden="true" />}
      {state.replace('_', ' ')}
    </span>
  );
}
